import { FeatureFlagKey } from '@/types';
import Iconify from '@/components/media/iconify';
import { useBoolean } from '@/hooks/state/use-boolean';
import { useFeatureFlag } from '@/hooks/data/use-feature-flag';
import { useOdooUnlinked } from '@/hooks/integrations/use-odoo-unlinked';
import ConfirmDialog from '@/components/overlays/custom-dialog/confirm-dialog';
import { OdooResourceType, isOdooProductItem } from '@/types/odoo.types';
import type { OdooSearchItem } from '@/types/odoo.types';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useTranslation } from 'react-i18next';

import { useState, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface OdooCreateLinkSectionProps {
  /** The Odoo entity type to search for unlinked records. */
  resourceType: OdooResourceType;
  /**
   * List of human-readable field labels that will be replaced by pre-fill.
   * Shown in the confirmation dialog.
   */
  fieldLabels: string[];
  /**
   * Called when the user confirms the pre-fill.
   * Receives the selected Odoo item so the parent can extract the ID and map fields.
   */
  onConfirmPreFill: (item: OdooSearchItem) => void;
  /** Called when the user clears / deselects the Odoo item. */
  onClear?: () => void;
  /** Whether to show a country mismatch warning. */
  countryWarning?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const odooFlagByResource: Record<OdooResourceType, FeatureFlagKey> = {
  [OdooResourceType.ORGANIZATIONS]: FeatureFlagKey.ENABLE_ODOO_ORGANIZATIONS_SYNC,
  [OdooResourceType.LEGAL_ENTITIES]: FeatureFlagKey.ENABLE_ODOO_LEGAL_ENTITIES_SYNC,
  [OdooResourceType.CONTACTS]: FeatureFlagKey.ENABLE_ODOO_CONTACT_SYNC,
  [OdooResourceType.PLANS]: FeatureFlagKey.ENABLE_ODOO_PRODUCTS_SYNC,
};

function getOptionLabel(option: OdooSearchItem): string {
  if (isOdooProductItem(option)) {
    const code = option.default_code ? ` (${option.default_code})` : '';
    return `${option.name}${code} — ID: ${option.id}`;
  }
  return `${option.name} — ID: ${option.id}`;
}

function getSecondaryText(option: OdooSearchItem): string {
  if (isOdooProductItem(option)) {
    return `SKU: ${option.default_code || '—'}`;
  }
  const email = typeof option.email === 'string' ? option.email : null;
  const vat = typeof option.vat === 'string' ? option.vat : null;
  return [email, vat].filter(Boolean).join(' | ') || '';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OdooCreateLinkSection({
  resourceType,
  fieldLabels,
  onConfirmPreFill,
  onClear,
  countryWarning,
}: OdooCreateLinkSectionProps) {
  const { t } = useTranslation();
  const { enabled: isOdooEnabled, isLoading: isFlagLoading } =
    useFeatureFlag(odooFlagByResource[resourceType]);

  const sectionExpanded = useBoolean(false);
  const confirmDialog = useBoolean(false);

  const [selectedItem, setSelectedItem] = useState<OdooSearchItem | null>(null);

  const { searchTerm, setSearchTerm, results, isFetching } = useOdooUnlinked({
    resourceType,
    enabled: sectionExpanded.value,
  });

  // ---- Handlers -----------------------------------------------------------

  const handleSelect = useCallback(
    (_event: unknown, value: OdooSearchItem | null) => {
      setSelectedItem(value);
      if (value) {
        // Open confirmation dialog before pre-filling
        confirmDialog.onTrue();
      } else {
        onClear?.();
      }
    },
    [confirmDialog, onClear]
  );

  const handleConfirm = useCallback(() => {
    if (selectedItem) {
      onConfirmPreFill(selectedItem);
    }
    confirmDialog.onFalse();
  }, [selectedItem, onConfirmPreFill, confirmDialog]);

  const handleCancel = useCallback(() => {
    // User declined — reset selection
    setSelectedItem(null);
    confirmDialog.onFalse();
    onClear?.();
  }, [confirmDialog, onClear]);

  const handleToggle = useCallback(() => {
    if (sectionExpanded.value) {
      // Collapsing — reset everything
      setSelectedItem(null);
      setSearchTerm('');
      onClear?.();
    }
    sectionExpanded.onToggle();
  }, [sectionExpanded, setSearchTerm, onClear]);

  // ---- Early returns ------------------------------------------------------

  if (isFlagLoading) return null;
  if (!isOdooEnabled) return null;

  // ---- Render -------------------------------------------------------------

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Iconify icon="mdi:swap-horizontal" width={22} />
              <Typography variant="subtitle1">
                {t('Link with existing Odoo record')}
              </Typography>
            </Stack>
          }
          action={
            <FormControlLabel
              control={
                <Switch
                  checked={sectionExpanded.value}
                  onChange={handleToggle}
                  size="small"
                />
              }
              label=""
            />
          }
        />

        {sectionExpanded.value && (
          <>
            <Divider />

            <CardContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'Search for an unlinked Odoo record to pre-fill form fields and link on creation.'
                  )}
                </Typography>

                <Autocomplete<OdooSearchItem, false, false, false>
                  value={selectedItem}
                  options={results}
                  loading={isFetching}
                  getOptionLabel={getOptionLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(x) => x}
                  noOptionsText={
                    searchTerm.length < 2
                      ? t('Type at least 2 characters to search')
                      : t('No results found')
                  }
                  onChange={handleSelect}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input') {
                      setSearchTerm(value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('Search unlinked Odoo records')}
                      placeholder={t('Type to search...')}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isFetching && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`Odoo ID: ${option.id}`}
                          {getSecondaryText(option) && ` — ${getSecondaryText(option)}`}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />

                {countryWarning && (
                  <Alert severity="warning" variant="outlined">
                    {countryWarning}
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </>
        )}
      </Card>

      {/* Confirmation dialog */}
      <ConfirmDialog
        open={confirmDialog.value}
        onClose={handleCancel}
        title={t('Pre-fill form fields?')}
        content={
          <Stack spacing={1}>
            <Typography variant="body2">
              {t('The following fields will be replaced with data from the selected Odoo record:')}
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              {fieldLabels.map((label) => (
                <Typography component="li" variant="body2" key={label}>
                  {label}
                </Typography>
              ))}
            </Box>
          </Stack>
        }
        action={
          <Button variant="contained" color="primary" onClick={handleConfirm}>
            {t('Continue')}
          </Button>
        }
      />
    </>
  );
}
